/**
 * Egzersiz kayıt defteri ve haftalık set üretimi.
 *
 * Program motoru buraya "bu çocuk için 3. hafta, dikkat ağırlıklı, 20
 * dakika, kağıt" der; karşılığında tam olarak o seti alır. Aynı girdiyle
 * aynı set üretilir — basılan ödev kaybolursa yeniden basılabilir.
 */

import { Rng, seedFrom } from '../prng'
import type { Domain } from '../types'
import {
  oddOneOut,
  pathTrace,
  scanTarget,
  spotDifference,
  trailMaking,
  wordSearch,
} from './templates/attention'
import {
  delayedRecall,
  listeningSteps,
  memoryPairs,
  readingTarget,
  spanRecall,
  symbolCode,
} from './templates/memory'
import {
  categorySort,
  logicGrid,
  matrixPattern,
  sequenceComplete,
  sudokuMini,
} from './templates/reasoning'
import {
  arithFluency,
  dotCopy,
  maze,
  mentalRotation,
  mirrorImage,
  oppositeRule,
} from './templates/spatial'
import { arithGrid, dualSequence } from './templates/nicel'
import { hiddenFigure, overlapFind } from './templates/figur'
import { ruleMaze, towerOfLondon } from './templates/yonetici'
import { codeLanguage, contradiction } from './templates/dil'
import { routePlan, schedulePlan } from './templates/gunluk'
import { eyeEmotion, falseBelief, socialCue } from './templates/sosyal'
import { balanceScale, zebraGrid } from './templates/mantik'
import { overlayLayers, paperFold } from './templates/uzam'
import { flowTrace } from './templates/akis'
import { alternativeUses, incompleteFigures } from './templates/yaratici'
import { rhythmTap, trackPath } from './templates/motor'
import { wordFluency, wordLadder } from './templates/sozel'
import type { Channel, ExerciseCode, ExerciseInstance, ExerciseTemplate } from './types'

export * from './types'
export * from './helpers'

export const EXERCISES: Record<ExerciseCode, ExerciseTemplate> = {
  scan_target: scanTarget,
  spot_difference: spotDifference,
  odd_one_out: oddOneOut,
  word_search: wordSearch,
  path_trace: pathTrace,
  span_recall: spanRecall,
  memory_pairs: memoryPairs,
  symbol_code: symbolCode,
  listening_steps: listeningSteps,
  reading_target: readingTarget,
  matrix_pattern: matrixPattern,
  sequence_complete: sequenceComplete,
  logic_grid: logicGrid,
  sudoku_mini: sudokuMini,
  category_sort: categorySort,
  maze,
  mental_rotation: mentalRotation,
  mirror_image: mirrorImage,
  arith_fluency: arithFluency,
  opposite_rule: oppositeRule,
  dual_sequence: dualSequence,
  arith_grid: arithGrid,
  trail_making: trailMaking,
  word_ladder: wordLadder,
  word_fluency: wordFluency,
  dot_copy: dotCopy,
  delayed_recall: delayedRecall,
  overlap_find: overlapFind,
  hidden_figure: hiddenFigure,
  tower_of_london: towerOfLondon,
  rule_maze: ruleMaze,
  code_language: codeLanguage,
  contradiction,
  schedule_plan: schedulePlan,
  route_plan: routePlan,
  false_belief: falseBelief,
  social_cue: socialCue,
  alternative_uses: alternativeUses,
  incomplete_figures: incompleteFigures,
  track_path: trackPath,
  rhythm_tap: rhythmTap,
  eye_emotion: eyeEmotion,
  zebra_grid: zebraGrid,
  balance_scale: balanceScale,
  overlay_layers: overlayLayers,
  paper_fold: paperFold,
  flow_trace: flowTrace,
}

export const EXERCISE_CODES = Object.keys(EXERCISES) as ExerciseCode[]

export function getExercise(code: ExerciseCode): ExerciseTemplate {
  const t = EXERCISES[code]
  if (!t) throw new Error(`Bilinmeyen egzersiz: ${code}`)
  return t
}

export function generateExercise(
  code: ExerciseCode,
  seed: number | bigint,
  level: number,
): ExerciseInstance {
  return getExercise(code).generate(seed, level)
}

export interface WeekPlan {
  /** Danışan + program + hafta: tohumun temeli */
  salt: string
  week: number
  /** Alan ağırlıkları; toplamı 1 olmak zorunda değil */
  focus: Partial<Record<Domain, number>>
  /** Hedef süre (dakika) */
  minutes: number
  channel: Channel
  /** Çocuğun o anki zorluk seviyesi (1–10) */
  level: number
}

export interface PlannedExercise {
  code: ExerciseCode
  seed: string
  level: number
  instance: ExerciseInstance
}

/**
 * Haftalık set üretir.
 *
 * Seçim kuralları:
 *  - Kanala uymayan şablon hiç değerlendirilmez (örn. eşleştirme belleği
 *    kağıda basılamaz).
 *  - Alan ağırlığı yüksek olan şablonlar daha sık seçilir, ama ağırlığı
 *    sıfır olan alanlar da tamamen dışlanmaz: tek alana kilitlenen bir
 *    program çocuğu sıkar ve transferi daraltır.
 *  - Aynı şablon bir haftada en fazla iki kez.
 *  - Süre bütçesi aşılırsa durur; en az bir egzersiz her zaman üretilir.
 */
export function planWeek(plan: WeekPlan): PlannedExercise[] {
  const budget = plan.minutes * 60
  const rng = new Rng(seedFrom('week', plan.salt, plan.week, plan.channel))

  const eligible = EXERCISE_CODES.filter((c) => EXERCISES[c].channels.includes(plan.channel))

  const weightOf = (code: ExerciseCode): number => {
    const t = EXERCISES[code]
    let w = 0.25 // taban: hiçbir alan tamamen dışlanmasın
    for (const d of t.domains) w += plan.focus[d] ?? 0
    return w
  }

  const out: PlannedExercise[] = []
  const used = new Map<ExerciseCode, number>()
  let spent = 0
  let guard = 0

  while (spent < budget && guard++ < 200) {
    const pool = eligible.filter((c) => (used.get(c) ?? 0) < 2)
    if (pool.length === 0) break

    // Ağırlıklı seçim
    const weights = pool.map(weightOf)
    const total = weights.reduce((a, b) => a + b, 0)
    let r = rng.float() * total
    let pick = pool[pool.length - 1]!
    for (let i = 0; i < pool.length; i++) {
      r -= weights[i]!
      if (r <= 0) {
        pick = pool[i]!
        break
      }
    }

    const seed = seedFrom('ex', plan.salt, plan.week, pick, out.length)
    const instance = generateExercise(pick, seed, plan.level)

    // Bütçeyi çok aşıyorsa ve elimizde bir şey varsa bu seçimi atla
    if (out.length > 0 && spent + instance.estimatedSeconds > budget * 1.15) {
      used.set(pick, 2)
      continue
    }

    out.push({ code: pick, seed: String(seed), level: plan.level, instance })
    used.set(pick, (used.get(pick) ?? 0) + 1)
    spent += instance.estimatedSeconds
  }

  return out
}

/** Haftalık setin toplam tahmini süresi (dakika) */
export function planMinutes(plan: PlannedExercise[]): number {
  return Math.round(plan.reduce((a, p) => a + p.instance.estimatedSeconds, 0) / 60)
}
